import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DriverDetails } from '../../types/predictions';
import { driverColors, MAX_SCATTER_SERIES, useChartTokens, useWidth } from './chartKit';

interface Props {
  driversDetails: Record<string, DriverDetails[]>;
}

interface Dot {
  driver: string;
  avg: number;
  pos: number;
  race: DriverDetails;
}

const HEIGHT = 300;
const M = { top: 16, right: 16, bottom: 36, left: 44 };

/**
 * Average running position vs finish, one dot per race. Dots below the diagonal finished worse than they ran (bad
 * luck, a late wreck, strategy); above it, better. Scatter plots show at most three drivers at once.
 */
export const SpeedVsResultChart: React.FC<Props> = ({ driversDetails }) => {
  const tokens = useChartTokens();
  const { ref, width } = useWidth();
  const drivers = Object.keys(driversDetails);
  const colors = driverColors(drivers, tokens.series);
  const [shown, setShown] = useState<string[]>(drivers.slice(0, MAX_SCATTER_SERIES));
  const [hover, setHover] = useState<Dot | null>(null);

  const dots = useMemo(
    () =>
      shown.flatMap((d) =>
        (driversDetails[d] ?? [])
          .filter((r) => Number.isFinite(r.Avg) && Number.isFinite(r.Pos))
          .map((r) => ({ driver: d, avg: r.Avg, pos: r.Pos, race: r })),
      ),
    [shown, driversDetails],
  );
  const maxPos = Math.max(10, ...dots.map((d) => Math.max(d.avg, d.pos)));
  const lim = Math.ceil(maxPos / 10) * 10;
  const size = Math.min(width - M.left - M.right, 520);
  const plot = Math.max(200, size);
  // 1st at the top-left (better is up and left on both axes).
  const sx = (v: number) => M.left + ((v - 1) / (lim - 1)) * plot;
  const sy = (v: number) => M.top + ((v - 1) / (lim - 1)) * (HEIGHT - M.top - M.bottom);
  const ticks = Array.from({ length: Math.floor(lim / 10) + 1 }, (_, i) => Math.max(1, i * 10));

  const toggle = (d: string) =>
    setShown((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : prev.length >= MAX_SCATTER_SERIES ? prev : [...prev, d],
    );

  const unlucky = (d: string) => {
    const mine = dots.filter((x) => x.driver === d);
    return mine.length ? mine.filter((x) => x.pos - x.avg >= 8).length : 0;
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={0.5}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1 }}
        role="group"
        aria-label="Drivers shown"
      >
        {drivers.map((d) => {
          const on = shown.includes(d);
          const full = !on && shown.length >= MAX_SCATTER_SERIES;
          return (
            <Box
              key={d}
              component="button"
              type="button"
              onClick={() => toggle(d)}
              disabled={full}
              aria-pressed={on}
              title={full ? `Up to ${MAX_SCATTER_SERIES} drivers at once` : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'transparent',
                color: on ? 'text.primary' : 'text.disabled',
                font: 'inherit',
                fontSize: '0.8125rem',
                cursor: full ? 'not-allowed' : 'pointer',
                '&:hover': { bgcolor: full ? 'transparent' : 'action.hover' },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: on ? colors.get(d) : 'transparent',
                  border: 2,
                  borderColor: colors.get(d),
                }}
              />
              {d}
            </Box>
          );
        })}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        The diagonal is "finished where it ran". Dots below it finished worse than they ran; above it, better.
      </Typography>
      <Box ref={ref} sx={{ position: 'relative', width: '100%' }}>
        <svg
          width={Math.min(width, M.left + plot + M.right)}
          height={HEIGHT}
          role="img"
          aria-label="Average running position against finish, one dot per race"
          style={{ display: 'block', overflow: 'visible' }}
          onMouseLeave={() => setHover(null)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={sx(t)} x2={sx(t)} y1={M.top} y2={HEIGHT - M.bottom} stroke={tokens.grid} />
              <line x1={M.left} x2={M.left + plot} y1={sy(t)} y2={sy(t)} stroke={tokens.grid} />
              <text x={sx(t)} y={HEIGHT - M.bottom + 14} textAnchor="middle" fontSize={11} fill={tokens.axisText}>
                {t}
              </text>
              <text x={M.left - 6} y={sy(t)} dy="0.32em" textAnchor="end" fontSize={11} fill={tokens.axisText}>
                {t}
              </text>
            </g>
          ))}
          {/* Diagonal: finished where it ran. */}
          <line
            x1={sx(1)}
            y1={sy(1)}
            x2={sx(lim)}
            y2={sy(lim)}
            stroke={tokens.axisText}
            strokeOpacity={0.6}
            strokeWidth={1}
          />
          <text x={M.left + plot / 2} y={HEIGHT - 4} textAnchor="middle" fontSize={11} fill={tokens.axisText}>
            Average running position
          </text>
          <text
            transform={`translate(12 ${M.top + (HEIGHT - M.top - M.bottom) / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize={11}
            fill={tokens.axisText}
          >
            Finish
          </text>
          {dots.map((d, i) => (
            <circle
              key={i}
              cx={sx(d.avg)}
              cy={sy(d.pos)}
              r={hover === d ? 6 : 4}
              fill={d.race.DNF ? tokens.surface : colors.get(d.driver)}
              stroke={d.race.DNF ? colors.get(d.driver) : tokens.surface}
              strokeWidth={2}
              onMouseEnter={() => setHover(d)}
              style={{ cursor: 'default' }}
            />
          ))}
          {/* Bigger invisible hit targets */}
          {dots.map((d, i) => (
            <circle
              key={`h${i}`}
              cx={sx(d.avg)}
              cy={sy(d.pos)}
              r={9}
              fill="transparent"
              onMouseEnter={() => setHover(d)}
            />
          ))}
        </svg>
        {hover && (
          <Box
            sx={{
              position: 'absolute',
              top: Math.max(0, sy(hover.pos) - 70),
              left: Math.min(sx(hover.avg) + 12, Math.max(8, width - 230)),
              width: 220,
              pointerEvents: 'none',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: 3,
              p: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {new Date(hover.race.Date).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}{' '}
              · {hover.race.Site}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {hover.driver}
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              Ran {hover.avg.toFixed(1)} on average, finished {hover.pos}
              {hover.race.DNF ? ' (DNF)' : ''}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        One dot per race; hollow dots are DNFs. Races that finished 8+ spots worse than they ran:{' '}
        {shown.map((d, i) => `${i ? ', ' : ''}${d} ${unlucky(d)}`).join('')}. A recent cluster below the line can mean a
        fast car with bad luck: a bounce-back candidate.
      </Typography>
    </Box>
  );
};
